"use client";

import { Badge, Box, Text, VStack } from "@chakra-ui/react";
import { memo, useCallback } from "react";
import type { MindstringPrototype } from "@/generated/fastapi";
import { useUserChoices } from "@/state/globalStore";
import WithSwipeDrag from "@/shared/ui/gestures/WithSwipeDrag";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import type { SelectedThoughtDocument } from "../../types";

type SortableStudioThoughtItemProps = {
  thought: SelectedThoughtDocument;
  isMobile: boolean;
  prototype: MindstringPrototype;
  selectionSituationId: string | null;
  textMinH?: string;
};

function SortableStudioThoughtItem({
  thought,
  isMobile,
  prototype,
  selectionSituationId,
  textMinH = "8",
}: SortableStudioThoughtItemProps) {
  const { isSelectedThought, toggleThoughtSelection, setInterimState } =
    useUserChoices();

  const handleReflectThought = useCallback(async () => {
    setInterimState("reflect_thought", { isOpen: true, passedObject: thought });
  }, [setInterimState, thought]);

  const handleToggle = useCallback(() => {
    toggleThoughtSelection({
      prototypeCategory: prototype,
      situationId: selectionSituationId,
      thoughtId: thought.objectID,
    });
  }, [
    prototype,
    selectionSituationId,
    thought.objectID,
    toggleThoughtSelection,
  ]);

  return (
    <WithSwipeDrag id={thought.objectID} isMobile={false}>
      <WithSwipeSelector
        isMobile={isMobile}
        selection={{
          isSelected: isSelectedThought(thought.objectID),
          onToggle: handleToggle,
          highlightWhenSelected: false,
          humanReadable: "Select thought for batch operations",
        }}
        action={{
          onSwipe: async () => {
            handleReflectThought();
          },
          icon: "reflect_thoughts",
          humanReadable: "Reflect on this thought",
          actionDescription: "Reflect",
        }}
        visual={{
          uncoveredBackgroundColor: "moveon.surface.bg",
        }}
      >
        <Box
          m="2"
          p="2.5"
          bg="moveon.surface.bg"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          ps={thought.situation_id ? "2.5" : "2"}
          borderInlineStartWidth={thought.situation_id ? "0" : "3px"}
          borderInlineStartColor={
            thought.situation_id ? "transparent" : "orange.400"
          }
        >
          <VStack align="stretch" gap="1">
            {!thought.situation_id && (
              <Badge variant="surface" colorPalette="orange" alignSelf="start">
                Unassigned
              </Badge>
            )}
            <Text
              minH={textMinH}
              fontSize="sm"
              lineHeight="tall"
              overflowWrap="anywhere"
              whiteSpace="pre-wrap"
            >
              {thought.mindstring}
            </Text>
          </VStack>
        </Box>
      </WithSwipeSelector>
    </WithSwipeDrag>
  );
}

export default memo(SortableStudioThoughtItem);
